/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect, useRef } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data';

import { fireTrackEvent, getActionName } from './AnalyticsEvents';
import type {
	ArchivePagesEndAnalyticsQuery as ArchivePagesEndAnalyticsQuery$data,
	ArchivePagesEndAnalyticsQueryVariables as ArchivePagesEndAnalyticsQuery$variables,
} from './__types__/ArchivePagesEndAnalyticsQuery';

export type ArchivePagesEndAnalyticsProps = {
	archivingError?: Error;
	archivingPercentageComplete: number;
	contentIds: string[];
	isArchive: boolean;
	isBulk: boolean;
	source: string;
	pageCount?: number;
	spaceId?: string | null;
	additionalTrackEventAttributes?: object;
};

export function ArchivePagesEndAnalytics({
	archivingError,
	archivingPercentageComplete,
	contentIds,
	isArchive,
	isBulk,
	source,
	pageCount,
	spaceId,
	additionalTrackEventAttributes,
}: ArchivePagesEndAnalyticsProps) {
	const analyticsFired = useRef<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition } = useSessionData();

	const { data, error } = useQuery<
		ArchivePagesEndAnalyticsQuery$data,
		ArchivePagesEndAnalyticsQuery$variables
	>(
		gql`
			query ArchivePagesEndAnalyticsQuery($spaceId: ID) {
				space(id: $spaceId) {
					id
					homepage {
						id
					}
					currentUser {
						isAdmin
					}
				}
			}
		`,
		{
			variables: {
				spaceId,
			},
		},
	);

	useEffect(() => {
		// Don't let an error in getting the space admin data keep us from firing the event
		if (data || error) {
			const isSpaceAdmin: boolean = data?.space?.currentUser?.isAdmin || false;
			const actionName = getActionName(isArchive, isBulk);
			const spaceOverviewId = data?.space?.homepage?.id;
			if ((!analyticsFired.current && archivingPercentageComplete === 100) || archivingError) {
				fireTrackEvent({
					createAnalyticsEvent,
					action: archivingError ? `${actionName}Failed` : `${actionName}Success`,
					actionSubject: 'page',
					actionSubjectId: isBulk ? 'multiple' : contentIds[0],
					source,
					containerType: 'space',
					containerId: spaceId,
					attributes: {
						pageCount,
						edition,
						contentId: isBulk ? undefined : contentIds[0],
						isSpaceOverview:
							spaceOverviewId && contentIds.includes(spaceOverviewId) ? true : undefined,
						...(!error && { isSpaceAdmin }),
						error: archivingError ? archivingError.message : undefined,
						...additionalTrackEventAttributes,
					},
				});
				analyticsFired.current = true;
			}
		}
	}, [
		analyticsFired,
		archivingError,
		archivingPercentageComplete,
		contentIds,
		createAnalyticsEvent,
		data,
		error,
		edition,
		isArchive,
		source,
		isBulk,
		pageCount,
		spaceId,
		additionalTrackEventAttributes,
	]);

	return null;
}
